import _ from "lodash";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { getValueBasedOnReportType } from "./reportTypeHelpers";

export const getIsEnableFinalGradeForIL = () =>
  ACLStore.can("FeatureFlag:EnableFinalGradeForIL");

export const getIsPdfCombinationEnabled = () =>
  ACLStore.can("FeatureFlag:CombineReportsToSinglePdfV2") ||
  ACLStore.can("FeatureFlag:CombineReportsToSinglePdf");

export const getIsGradeSummaryEnabledForProjects = ({
  node,
  nodes,
  targetChildUID = "GRADE_SUMMARY",
}) => {
  let isGradeSummaryEnabled = false;
  let gradeSummaryNodeId = "";
  const children = _.get(node, "children", []);
  _.forEach(children, childId => {
    const childNode = nodes[childId];
    const childUid = _.get(childNode, "uid", "");
    if (childUid === targetChildUID) {
      isGradeSummaryEnabled = _.get(childNode, "isEnabled", false);
      gradeSummaryNodeId = _.get(childNode, "id", "");
      return false;
    }
  });
  return { isGradeSummaryEnabled, gradeSummaryNodeId };
};

export const getIsProgressReportEnabled = ({ state }) => {
  const isCurrentAcademicYear = _.get(
    state,
    "platform.academicYearSelected.isCurrentAcademicYear",
    true
  );

  return (
    getIsAdminByRole() ||
    ACLStore.can("FeatureFlag:ShowProgressReportForPreviousYear") ||
    isCurrentAcademicYear
  );
};

export const getIsCoverSheetV2 = ({ coverSheetNodes }) => {
  return _.get(
    coverSheetNodes,
    "coverSheetTemplate.create.data.modalValue",
    null
  );
};

export const getIsGradeScaleNewTemplate = ({ nodes }) => {
  return _.some(nodes, { id: "settings=>assessmentCriteria" });
};

export const canOverrideScoreInReports = () =>
  ACLStore.can("FeatureFlag:OverrideScoresInGradebook");

export const isNewDecimalSettingEnabled = () => {
  return ACLStore.can("FeatureFlag:IsEnableGradingSetupDecimalConfig");
};

//This function is used to update the display sequence property of the nodes

export const getIsScoreAndGradeAggregationEnabled = () => {
  return getValueBasedOnReportType()
    ? ACLStore.can("FeatureFlag:ScoreGradeAggregationV2")
    : ACLStore.can("FeatureFlag:ScoreAndGradeAggregation");
};

export const getIsGpaAndCreditsEnabled = () => {
  return getValueBasedOnReportType()
    ? ACLStore.can("FeatureFlag:GpaCreditsV2Enhancements")
    : ACLStore.can("FeatureFlag:EnableGpaAndCredits");
};

// This function returns the property key for the criteriaEnabledInProgressSummary field of the "ps=>aggregateScores" node.
// This property determines which criteria sets are enabled in the progress summary for a particular aggregate type.
// Sample structure: criteriaEnabledInProgressSummary: { averageCriteriaSetIds: [], totalCriteriaSetIds: [] }

export const getIsMultiTermReportEnabledForNewGradingSetup = () =>
  ACLStore.can("FeatureFlag:ReportMultiterm2.0") ||
  getValueBasedOnReportType({
    courseValue: false,
    subjectValue: false,
    defaultValue: true,
  });

export const getIsEditCriteriaSetLabelFeatureEnabled = () =>
  ACLStore.can("FeatureFlag:ProgressReportEditableCriteriaSetLabels");

export const getIsProjectGradebookInReportsEnabled = ({
  projectsVersion = "",
  isFreezed = false,
} = {}) => {
  if (isFreezed) {
    return (
      projectsVersion === "2.0" &&
      ACLStore.can("FeatureFlag:EnableProjectGradebookInReports")
    );
  } else {
    return ACLStore.can("FeatureFlag:EnableProjectGradebookInReports");
  }
};

export const getIsRnpConsumptionEnabled = () => {
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
};

export const getIsProgressionPointEnabled = () =>
  ACLStore.can("FeatureFlag:ProgressionPoint");

export const getIsReportRevampPhase2Enabled = () =>
  ACLStore.can("FeatureFlag:isReportRevampPhase2Enabled");

export const getIsMYPCriteriaTotalAsAggregateEnabled = () => {
  return ACLStore.can("FeatureFlag:MYPCriteriaTotalAsAggregate");
};

export const getShowUnitConfigurationInReports = () =>
  ACLStore.can("FeatureFlag:ShowUnitConfigurationInReports");

export const isChatGPTPoweredTextFeaturesEnabled = () =>
  ACLStore.can("Common:ChatGPTPoweredTextFeatures");

export const getIsCriteriaDeletionEnabledInReport = () =>
  ACLStore.can(
    "FeatureFlag:AllowDeletionOfCriteriaSetsInProgressReportTemplate"
  );

export const getIsStudentReflectionEnabledInReports = () =>
  ACLStore.can("FeatureFlag:EnableStudentReflectionInProgressReports");

// If all ratings in setOptions are single digit positive numbers (0-9) and all are unique
// then ratings can be used as shortcuts in the UI

export const isAttendanceSettingsV2Enabled = () =>
  ACLStore.can("FeatureFlag:AttendanceSettingsV2");

export const getIsStandardsV2Enabled = () =>
  ACLStore.can("AdminPortal:StandardsV2");

export const showSetupSectionInAttendanceInProgressReport = () =>
  ACLStore.can("FeatureFlag:ShowSetupSectionInAttendanceInProgressReport");

export const isSkillEnabledInNewReports = () =>
  ACLStore.can("FeatureFlag:EnableLearningGoalsEvaluationInNewGradingSetup") &&
  getValueBasedOnReportType();

export const getIsGradingHistoryEnhancementsEnabledInReports = () =>
  ACLStore.can("FeatureFlag:GradingHistoryEnhancementReport");

export const getHidePercentageInFinalScoreInReports = () =>
  ACLStore.can("FeatureFlag:HidePercentageInFinalScoreInReports");

export const getIsNewAttendanceSettingsEnabled = () =>
  ACLStore.can("FeatureFlag:AttendanceSettingsV2") ||
  ACLStore.can("FeatureFlag:Toddle2.0Attendance");

export const shouldHideAttendanceSetupSectionInProgressReportTemplate = () =>
  getValueBasedOnReportType() ||
  !getIsNewAttendanceSettingsEnabled() ||
  (getIsNewAttendanceSettingsEnabled() &&
    !showSetupSectionInAttendanceInProgressReport());

export const getHideDownloadOptionInProgressReportSet = () =>
  ACLStore.can("FeatureFlag:HideDownloadOptionInProgressReportSet");

export const getIsOptimisedProgressReportGetRating = () =>
  ACLStore.can("FeatureFlag:OptimisedProgressReportGetRating");
